import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Mail,
  Calendar,
  ArrowLeft,
  Plus,
  Loader2,
  Building2,
  Wrench,
  DollarSign,
  Clock,
  RefreshCw,
  CalendarClock,
  Play,
  Settings2,
  Trash2,
  Eye,
  CheckCircle2,
  XCircle,
  MailOpen,
  MousePointerClick,
  TrendingUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function AgentReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [bounceAlerts, setBounceAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('reports');
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [deliveryStats, setDeliveryStats] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  
  const [generateForm, setGenerateForm] = useState({
    hoa_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    report_type: 'monthly'
  });

  const [scheduleForm, setScheduleForm] = useState({
    hoa_id: '',
    frequency: 'monthly',
    day_of_month: 1,
    recipient_emails: '',
    include_trustees: true,
    include_managers: true,
    auto_send: true,
    custom_message: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch buildings for this agent
      const buildingsRes = await fetch(`${API_URL}/api/managing-agents/my-buildings`, { 
        credentials: 'include' 
      });
      if (buildingsRes.ok) {
        const data = await buildingsRes.json();
        setBuildings(data || []);
        
        // Fetch reports for all buildings
        const allReports = [];
        for (const building of (data || [])) {
          const reportsRes = await fetch(`${API_URL}/api/agent-features/reports/list/${building.hoa_id}`, { 
            credentials: 'include' 
          });
          if (reportsRes.ok) {
            const reports = await reportsRes.json();
            allReports.push(...reports);
          }
        }
        setReports(allReports.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
      
      // Fetch all schedules
      const schedulesRes = await fetch(`${API_URL}/api/agent-features/reports/schedules/all`, { 
        credentials: 'include' 
      });
      if (schedulesRes.ok) {
        setSchedules(await schedulesRes.json());
      }
      
      // Fetch overall delivery stats
      const statsRes = await fetch(`${API_URL}/api/agent-features/reports/delivery-stats`, { 
        credentials: 'include' 
      });
      if (statsRes.ok) {
        setDeliveryStats(await statsRes.json());
      }
      
      // Fetch bounce alerts
      const alertsRes = await fetch(`${API_URL}/api/agent-features/bounce-alerts`, { 
        credentials: 'include' 
      });
      if (alertsRes.ok) {
        setBounceAlerts(await alertsRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!generateForm.hoa_id) {
      toast.error('Please select a building');
      return;
    }
    
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/agent-features/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(generateForm)
      });
      
      if (res.ok) {
        toast.success('Report generated successfully');
        setShowGenerateDialog(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPDF = async (reportId) => {
    window.open(`${API_URL}/api/agent-features/reports/${reportId}/pdf`, '_blank');
    toast.success('Report downloading...');
  };

  const handleEmailReport = async () => {
    if (!emailRecipients.trim()) {
      toast.error('Please enter recipient email addresses');
      return;
    }
    
    const emails = emailRecipients.split(',').map(e => e.trim()).filter(Boolean);
    
    try {
      const res = await fetch(`${API_URL}/api/agent-features/reports/${selectedReport.report_id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          report_id: selectedReport.report_id,
          recipient_emails: emails,
          include_message: emailMessage || null
        })
      });
      
      if (res.ok) {
        toast.success(`Report sent to ${emails.length} recipients`);
        setShowEmailDialog(false);
        setEmailRecipients('');
        setEmailMessage('');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to send report');
    }
  };

  const handleCreateSchedule = async () => {
    if (!scheduleForm.hoa_id) {
      toast.error('Please select a building');
      return;
    }
    
    setGenerating(true);
    try {
      const emailList = scheduleForm.recipient_emails
        ? scheduleForm.recipient_emails.split(',').map(e => e.trim()).filter(Boolean)
        : [];
      
      const res = await fetch(`${API_URL}/api/agent-features/reports/schedule/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...scheduleForm,
          recipient_emails: emailList
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Auto-report scheduled! Next run: ${new Date(data.next_run).toLocaleDateString()}`);
        setShowScheduleDialog(false);
        setScheduleForm({
          hoa_id: '',
          frequency: 'monthly',
          day_of_month: 1,
          recipient_emails: '',
          include_trustees: true,
          include_managers: true,
          auto_send: true,
          custom_message: ''
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create schedule');
      }
    } catch (error) {
      toast.error('Failed to create schedule');
    } finally {
      setGenerating(false);
    }
  };

  const handleRunScheduleNow = async (scheduleId) => {
    try {
      const res = await fetch(`${API_URL}/api/agent-features/reports/schedule/${scheduleId}/run`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Report generated and sent to ${data.recipients_count} recipients`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to run scheduled report');
    }
  };

  const handleViewDeliveryStatus = async (report) => {
    setSelectedReport(report);
    try {
      const res = await fetch(`${API_URL}/api/agent-features/reports/${report.report_id}/delivery-status`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setDeliveryStatus(data);
        setShowDeliveryDialog(true);
      }
    } catch (error) {
      toast.error('Failed to fetch delivery status');
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/agent-features/bounce-alerts/${alertId}/acknowledge`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Alert acknowledged');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleResolveAlert = async () => {
    if (!selectedAlert) return;
    
    try {
      const params = new URLSearchParams();
      if (newEmail) params.append('new_email', newEmail);
      
      const res = await fetch(`${API_URL}/api/agent-features/bounce-alerts/${selectedAlert.alert_id}/resolve?${params}`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.email_updated ? 'Alert resolved and email updated' : 'Alert resolved');
        setShowResolveDialog(false);
        setSelectedAlert(null);
        setNewEmail('');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleToggleSchedule = async (schedule) => {
    try {
      const res = await fetch(`${API_URL}/api/agent-features/reports/schedule/${schedule.schedule_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_enabled: !schedule.is_enabled })
      });
      
      if (res.ok) {
        toast.success(schedule.is_enabled ? 'Schedule paused' : 'Schedule enabled');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm('Delete this auto-report schedule?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/agent-features/reports/schedule/${scheduleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Schedule deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const formatCurrency = (amount) => {
    return `R ${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const getCollectionColor = (pct) => {
    if (pct >= 80) return 'text-emerald-600';
    if (pct >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="agent-reports">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Automated Reports</h1>
              <p className="text-slate-500 mt-1">Generate and schedule monthly reports for trustees</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" onClick={() => setShowScheduleDialog(true)}>
                <CalendarClock className="w-4 h-4 mr-2" />
                Schedule
              </Button>
              <Button onClick={() => setShowGenerateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Generate Report
              </Button>
            </div>
          </div>
        </div>

        {/* Delivery Stats Summary */}
        {deliveryStats && deliveryStats.total > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{deliveryStats.total}</p>
                    <p className="text-xs text-slate-500">Emails Sent (30d)</p>
                  </div>
                  <Mail className="w-8 h-8 text-indigo-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{deliveryStats.delivery_rate}%</p>
                    <p className="text-xs text-slate-500">Delivery Rate</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{deliveryStats.open_rate}%</p>
                    <p className="text-xs text-slate-500">Open Rate</p>
                  </div>
                  <MailOpen className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{deliveryStats.click_rate}%</p>
                    <p className="text-xs text-slate-500">Click Rate</p>
                  </div>
                  <MousePointerClick className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="reports">Reports ({reports.length})</TabsTrigger>
            <TabsTrigger value="schedules">Auto-Schedules ({schedules.length})</TabsTrigger>
            <TabsTrigger value="bounces" className="relative">
              Bounce Alerts
              {bounceAlerts.filter(a => a.status !== 'resolved').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {bounceAlerts.filter(a => a.status !== 'resolved').length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Reports Tab */}
          <TabsContent value="reports">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : reports.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No Reports Yet</h3>
                  <p className="text-slate-500 mb-4">Generate your first automated report for trustees.</p>
                  <Button onClick={() => setShowGenerateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reports.map(report => (
                  <Card key={report.report_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="w-5 h-5 text-indigo-600" />
                            <h3 className="font-semibold text-lg">{report.hoa_name}</h3>
                            <Badge variant="outline">{report.report_type}</Badge>
                            {report.status === 'sent' && (
                              <Badge className="bg-emerald-100 text-emerald-700">Sent</Badge>
                            )}
                          </div>
                          <p className="text-slate-500 text-sm mb-3">
                            {MONTHS[report.report_month - 1]} {report.report_year}
                          </p>
                          
                          {/* Metrics Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-slate-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                <Wrench className="w-3 h-3" />
                                Maintenance
                              </div>
                              <p className="font-semibold">{report.tickets_resolved}/{report.tickets_submitted} resolved</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                <DollarSign className="w-3 h-3" />
                                Collection
                              </div>
                              <p className={`font-semibold ${getCollectionColor(report.levy_collection_percentage)}`}>
                                {report.levy_collection_percentage}%
                              </p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                <Clock className="w-3 h-3" />
                                Avg Resolution
                              </div>
                              <p className="font-semibold">{report.avg_resolution_days} days</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                <DollarSign className="w-3 h-3" />
                                Arrears
                              </div>
                              <p className="font-semibold text-red-600">{formatCurrency(report.total_arrears)}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-row lg:flex-col gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(report.report_id)}>
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-indigo-600 hover:bg-indigo-700"
                            onClick={() => {
                              setSelectedReport(report);
                              setShowEmailDialog(true);
                            }}
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            Email
                          </Button>
                          {report.status === 'sent' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleViewDeliveryStatus(report)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Status
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Schedules Tab */}
          <TabsContent value="schedules">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : schedules.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <CalendarClock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No Auto-Schedules</h3>
                  <p className="text-slate-500 mb-4">Set up automatic monthly reports for your buildings.</p>
                  <Button onClick={() => setShowScheduleDialog(true)}>
                    <CalendarClock className="w-4 h-4 mr-2" />
                    Create Schedule
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {schedules.map(schedule => (
                  <Card key={schedule.schedule_id} className={`${!schedule.is_enabled ? 'opacity-60' : ''}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-indigo-600" />
                            <h3 className="font-semibold">{schedule.hoa_name}</h3>
                          </div>
                          <Badge variant="outline" className="mt-1">{schedule.frequency}</Badge>
                        </div>
                        <Switch 
                          checked={schedule.is_enabled}
                          onCheckedChange={() => handleToggleSchedule(schedule)}
                        />
                      </div>
                      
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Next Run:</span>
                          <span className="font-medium">{formatDate(schedule.next_run)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Last Run:</span>
                          <span>{schedule.last_run ? formatDate(schedule.last_run) : 'Never'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Reports Sent:</span>
                          <span>{schedule.run_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Auto-send:</span>
                          <span>{schedule.auto_send ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleRunScheduleNow(schedule.schedule_id)}
                          disabled={!schedule.is_enabled}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Run Now
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Bounce Alerts Tab */}
          <TabsContent value="bounces">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : bounceAlerts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <CheckCircle2 className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No Bounce Alerts</h3>
                  <p className="text-slate-500">All emails are being delivered successfully!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bounceAlerts.map(alert => (
                  <Card key={alert.alert_id} className={`border-l-4 ${
                    alert.status === 'new' ? 'border-l-red-500' :
                    alert.status === 'acknowledged' ? 'border-l-amber-500' :
                    'border-l-emerald-500'
                  }`}>
                    <CardContent className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <XCircle className="w-5 h-5 text-red-500" />
                            <h3 className="font-semibold">{alert.bounced_email}</h3>
                            <Badge className={
                              alert.status === 'new' ? 'bg-red-100 text-red-700' :
                              alert.status === 'acknowledged' ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                            }>
                              {alert.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-slate-500 space-y-1">
                            <p><Building2 className="w-3 h-3 inline mr-1" />{alert.hoa_name}</p>
                            {alert.user_name && <p>Contact: {alert.user_name} ({alert.user_role})</p>}
                            <p>Reason: {alert.bounce_reason || 'Unknown'}</p>
                            <p>Bounced: {formatDate(alert.bounced_at)}</p>
                            {alert.new_email && (
                              <p className="text-emerald-600">Updated to: {alert.new_email}</p>
                            )}
                          </div>
                        </div>
                        
                        {alert.status !== 'resolved' && (
                          <div className="flex gap-2">
                            {alert.status === 'new' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleAcknowledgeAlert(alert.alert_id)}
                              >
                                Acknowledge
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => {
                                setSelectedAlert(alert);
                                setShowResolveDialog(true);
                              }}
                            >
                              Resolve
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Generate Report Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Generate Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Building *</Label>
                <Select 
                  value={generateForm.hoa_id} 
                  onValueChange={(v) => setGenerateForm({...generateForm, hoa_id: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select building" />
                  </SelectTrigger>
                  <SelectContent>
                    {buildings.map(b => (
                      <SelectItem key={b.hoa_id} value={b.hoa_id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Month</Label>
                  <Select 
                    value={generateForm.month.toString()} 
                    onValueChange={(v) => setGenerateForm({...generateForm, month: parseInt(v)})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => (
                        <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year</Label>
                  <Select 
                    value={generateForm.year.toString()} 
                    onValueChange={(v) => setGenerateForm({...generateForm, year: parseInt(v)})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Report Type</Label>
                <Select 
                  value={generateForm.report_type} 
                  onValueChange={(v) => setGenerateForm({...generateForm, report_type: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
              <Button onClick={handleGenerateReport} disabled={generating}>
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Report'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Report Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Email Report to Trustees</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Recipient Email Addresses *</Label>
                <Input
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e.target.value)}
                  placeholder="trustee1@email.com, trustee2@email.com"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Separate multiple emails with commas</p>
              </div>
              <div>
                <Label>Message (optional)</Label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Add a personal message to accompany the report..."
                  className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
              <Button onClick={handleEmailReport}>
                <Mail className="w-4 h-4 mr-2" />
                Send Report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Schedule Dialog */}
        <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Schedule Automatic Reports</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Building *</Label>
                <Select 
                  value={scheduleForm.hoa_id} 
                  onValueChange={(v) => setScheduleForm({...scheduleForm, hoa_id: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select building" />
                  </SelectTrigger>
                  <SelectContent>
                    {buildings.filter(b => !schedules.find(s => s.hoa_id === b.hoa_id)).map(b => (
                      <SelectItem key={b.hoa_id} value={b.hoa_id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Frequency</Label>
                  <Select 
                    value={scheduleForm.frequency} 
                    onValueChange={(v) => setScheduleForm({...scheduleForm, frequency: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Day of Month</Label>
                  <Select 
                    value={scheduleForm.day_of_month.toString()} 
                    onValueChange={(v) => setScheduleForm({...scheduleForm, day_of_month: parseInt(v)})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 5, 10, 15, 20, 25].map(d => (
                        <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Additional Recipients (optional)</Label>
                <Input
                  value={scheduleForm.recipient_emails}
                  onChange={(e) => setScheduleForm({...scheduleForm, recipient_emails: e.target.value})}
                  placeholder="extra@email.com, another@email.com"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Comma-separated emails. Trustees are auto-included.</p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Include all trustees</Label>
                  <Switch 
                    checked={scheduleForm.include_trustees}
                    onCheckedChange={(v) => setScheduleForm({...scheduleForm, include_trustees: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Include property managers</Label>
                  <Switch 
                    checked={scheduleForm.include_managers}
                    onCheckedChange={(v) => setScheduleForm({...scheduleForm, include_managers: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Auto-send emails</Label>
                  <Switch 
                    checked={scheduleForm.auto_send}
                    onCheckedChange={(v) => setScheduleForm({...scheduleForm, auto_send: v})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Custom Message (optional)</Label>
                <textarea
                  value={scheduleForm.custom_message}
                  onChange={(e) => setScheduleForm({...scheduleForm, custom_message: e.target.value})}
                  placeholder="Monthly message to include with reports..."
                  className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateSchedule} disabled={generating}>
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Schedule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delivery Status Dialog */}
        <Dialog open={showDeliveryDialog} onOpenChange={setShowDeliveryDialog}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Email Delivery Status</DialogTitle>
            </DialogHeader>
            {deliveryStatus && (
              <div className="py-4">
                {/* Summary */}
                {deliveryStatus.summary && (
                  <div className="grid grid-cols-4 gap-3 mb-6">
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-600">{deliveryStatus.summary.delivered}</p>
                      <p className="text-xs text-slate-500">Delivered</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600">{deliveryStatus.summary.opened}</p>
                      <p className="text-xs text-slate-500">Opened</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-600">{deliveryStatus.summary.clicked}</p>
                      <p className="text-xs text-slate-500">Clicked</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-600">{deliveryStatus.summary.bounced}</p>
                      <p className="text-xs text-slate-500">Bounced</p>
                    </div>
                  </div>
                )}
                
                {/* Recipients List */}
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  <Label className="mb-2 block">Recipients ({deliveryStatus.recipients?.length || 0})</Label>
                  {deliveryStatus.recipients?.map((r, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{r.recipient_name || r.recipient_email}</p>
                        {r.recipient_name && <p className="text-xs text-slate-500">{r.recipient_email}</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Status badge */}
                        <Badge className={
                          r.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                          r.status === 'bounced' ? 'bg-red-100 text-red-700' :
                          r.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-600'
                        }>
                          {r.status}
                        </Badge>
                        
                        {/* Opened indicator */}
                        {r.opened && (
                          <div className="flex items-center gap-1 text-blue-600" title={`Opened ${r.open_count} times`}>
                            <MailOpen className="w-4 h-4" />
                            <span className="text-xs">{r.open_count}</span>
                          </div>
                        )}
                        
                        {/* Clicked indicator */}
                        {r.clicked && (
                          <div className="flex items-center gap-1 text-purple-600" title={`Clicked ${r.click_count} times`}>
                            <MousePointerClick className="w-4 h-4" />
                            <span className="text-xs">{r.click_count}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeliveryDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Resolve Bounce Alert Dialog */}
        <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Resolve Bounce Alert</DialogTitle>
            </DialogHeader>
            {selectedAlert && (
              <div className="py-4">
                <div className="bg-red-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-700">
                    <strong>Bounced Email:</strong> {selectedAlert.bounced_email}
                  </p>
                  <p className="text-sm text-red-600 mt-1">
                    <strong>Reason:</strong> {selectedAlert.bounce_reason || 'Unknown'}
                  </p>
                </div>
                
                <div>
                  <Label>New Email Address (optional)</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Enter the correct email to update this contact's address
                  </p>
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="corrected@email.com"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowResolveDialog(false);
                setSelectedAlert(null);
                setNewEmail('');
              }}>
                Cancel
              </Button>
              <Button onClick={handleResolveAlert} className="bg-emerald-600 hover:bg-emerald-700">
                {newEmail ? 'Update & Resolve' : 'Mark Resolved'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
